var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from "react";
import { classNames } from "@bookingcom/bui-core/utilities/classNames";
import mixinClassNames from "../../utilities/mixinClassNames.js";
import { mixinStyles } from "@bookingcom/bui-core/utilities/styles";
import { getElementScroll } from "@bookingcom/bui-core/utilities/scroll";
import TableRow from "./TableRow.js";
import TableHead from "./TableHead.js";
import styles from "@bookingcom/bui-core/css/Table.module.css";
var Table = function (props) {
    var className = props.className, attributes = props.attributes, rows = props.rows, headings = props.headings, checkboxName = props.checkboxName, stickyHeader = props.stickyHeader, borderless = props.borderless, compact = props.compact, viewLessLabel = props.viewLessLabel, viewMoreLabel = props.viewMoreLabel, onChange = props.onChange, onChangeAll = props.onChangeAll, verticalAlign = props.verticalAlign, _a = props.overflowFade, overflowFade = _a === void 0 ? true : _a, controlledValue = props.value, _b = props.defaultValue, defaultValue = _b === void 0 ? [] : _b, mixin = props.mixin;
    var _c = React.useState(defaultValue || []), value = _c[0], setValue = _c[1];
    var isControlled = controlledValue !== undefined;
    var usedValue = isControlled ? controlledValue : value;
    var _d = React.useState(null), fadeSide = _d[0], setFadeSide = _d[1];
    var scrollRef = React.useRef(null);
    var hasCollapsibleRows = rows.findIndex(function (row) { return row.collapsedContent; }) !== -1;
    var rootClassName = classNames(styles.root, className, overflowFade && fadeSide && styles["root--faded-".concat(fadeSide)], borderless && styles["root--borderless"], compact && styles["root--compact"], stickyHeader && styles["root--sticky-header"], mixinClassNames(mixin));
    var rootAttributes = __assign(__assign({}, attributes), { style: __assign(__assign({}, ((attributes === null || attributes === void 0 ? void 0 : attributes.style) || {})), mixinStyles(mixin)) });
    var handleRowSelect = function (_a) {
        var rowValue = _a.value, checked = _a.checked, event = _a.event, name = _a.name;
        var nextValue;
        if (checked) {
            nextValue = __spreadArray(__spreadArray([], usedValue, true), [rowValue], false);
        }
        else {
            nextValue = usedValue.filter(function (v) { return v !== rowValue; });
        }
        if (!isControlled)
            setValue(nextValue);
        if (onChange) {
            onChange({ name: name, value: nextValue, event: event });
        }
    };
    var handleSelectAll = function (_a) {
        var checked = _a.checked, event = _a.event;
        if (!checkboxName)
            return;
        var nextValue = checked ? rows.map(function (row) { return row.checkboxValue; }) : [];
        if (!isControlled)
            setValue(nextValue);
        if (onChange)
            onChange({ name: checkboxName, value: nextValue, event: event });
        if (onChangeAll) {
            onChangeAll({ name: checkboxName, value: nextValue, event: event });
        }
    };
    var updateFade = React.useCallback(function () {
        var scrollEl = scrollRef.current;
        if (!scrollEl)
            return;
        var scrollValue = getElementScroll(scrollEl);
        var hasLeftContent = scrollValue > 0;
        var hasRightContent = scrollValue + scrollEl.clientWidth < scrollEl.scrollWidth;
        if (hasLeftContent && hasRightContent)
            return setFadeSide("both");
        if (hasRightContent)
            return setFadeSide("end");
        if (hasLeftContent)
            return setFadeSide("start");
        setFadeSide(null);
    }, []);
    React.useEffect(function () {
        var scrollEl = scrollRef.current;
        if (!scrollEl)
            return;
        updateFade();
        window.addEventListener("resize", updateFade);
        scrollEl.addEventListener("scroll", updateFade);
        return function () {
            window.removeEventListener("resize", updateFade);
            scrollEl.removeEventListener("scroll", updateFade);
        };
    }, [updateFade]);
    return (React.createElement("div", __assign({}, rootAttributes, { className: rootClassName }),
        React.createElement("div", { className: styles.scroll, ref: scrollRef },
            React.createElement("table", { className: styles.table },
                React.createElement(TableHead, { hasCollapsibleRows: hasCollapsibleRows, headings: headings, checkboxName: checkboxName, rowsAmount: rows.length, value: usedValue, onSelectAll: checkboxName ? handleSelectAll : undefined }),
                React.createElement("tbody", { className: styles.body }, rows.map(function (row, index) {
                    var selected = !!row.checkboxValue &&
                        usedValue.indexOf(row.checkboxValue) !== -1;
                    return (React.createElement(TableRow, __assign({}, row, { hasCollapsibleRows: hasCollapsibleRows, key: row.key || row.id || index, headings: headings, checkboxName: checkboxName, selected: selected, onRowSelect: checkboxName ? handleRowSelect : undefined, viewLessLabel: viewLessLabel, viewMoreLabel: viewMoreLabel, verticalAlign: verticalAlign })));
                }))))));
};
export default Table;
